import loginService from './login/service/loginService.js'
import { createRouter, createWebHistory } from 'vue-router'

import userStore from './common/service/userStore.js'
import pageView from './common/service/pageView.js'
import { setRouter, goToApp } from './common/service/navigation.js'
const AuthLayoutComponent = () => import('./login/component/authLayoutComponent.vue')
const LoginComponent = () => import('./login/component/loginComponent.vue')
const ForgotPasswordComponent = () => import('./login/component/forgotPasswordComponent.vue')
const EditProfileComponent = () => import('./editProfile/component/editProfileComponent.vue')
const ResourceSettingsComponent = () => import('./resourceSettings/component/resourceSettingsComponent.vue')
const ClientRefreshComponent = () => import('./clientRefresh/component/clientRefreshComponent.vue')
const PrefixComponent = () => import('./prefix/component/prefixComponent.vue')
const QuoteTemplateComponent = () => import('./quoteTemplate/component/quoteTemplateComponent.vue')
const TestExternalComponent = () => import('./testExternal/component/testExternalComponent.vue')
const ReportingComponent = () => import('./reporting/component/reportingComponent.vue')
const DocumentComponent = () => import('./document/component/documentComponent.vue')
const DiagnosticsComponent = () => import('./diagnostics/component/diagnosticsComponent.vue')
const QuoteComponent = () => import('./quote/component/quoteComponent.vue')
const ResourcesLayoutComponent = () => import('./resources/component/resourcesLayoutComponent.vue')
const SettingsComponent = () => import('./settings/component/settingsComponent.vue')
const UsersComponent = () => import('./settings/component/usersComponent.vue')
const SecurityComponent = () => import('./settings/component/securityComponent.vue')

// Route components are lazy imports: the initial bundle carries the shell/login and only the
// page being opened, not every page. The names above are the route-level chunk boundaries.

// The routes vue-router owns. Two groups:
//
//   * auth (step 20) - Vue renders the whole screen, because there is no Angular shell on an
//     auth path. `meta.authRoot` is what tells the root instance below to render it.
//   * settings (step 21) - the Angular `abstract-mainLayoutNoSearch` shell still renders (menu,
//     profile, left calendar, footer) and the tab content is a <router-view> inside it, so the
//     root must NOT render these. `meta.page` is the directory the page-view recorder credits.
//
// Names and paths: the tab names are the ones SettingsComponent's nav uses.
const routes = [
  {
    path: '/login',
    component: AuthLayoutComponent,
    meta: { authRoot: true },
    children: [{ path: '', name: 'login', component: LoginComponent }],
  },
  {
    path: '/forgotPassword',
    component: AuthLayoutComponent,
    meta: { authRoot: true },
    children: [{ path: '', name: 'forgotPassword', component: ForgotPasswordComponent }],
  },
  // Settings: the tab shell (nav + Back to Resources) plus one route per tab. The tabs used to
  // render into a <router-view> inside the Angular `router-settings` state's settings.html; now
  // that App.vue is the only shell, SettingsComponent is a route component and the tabs are its
  // children (step 24). `activeTab` in SettingsComponent still reads `/settings/<name>`.
  {
    path: '/settings',
    component: SettingsComponent,
    children: [
      // The old catch-all rendered the shell for `/settings` with no tab; send it to profile.
      { path: '', redirect: { name: 'settings-profile' } },
      {
        path: 'profile',
        name: 'settings-profile',
        component: EditProfileComponent,
        meta: { page: 'editProfile' },
      },
      {
        path: 'users',
        name: 'settings-users',
        component: UsersComponent,
        meta: { page: 'users' },
      },
      {
        path: 'security',
        name: 'settings-security',
        component: SecurityComponent,
        meta: { page: 'security' },
      },
      {
        path: 'resources',
        name: 'settings-resources',
        component: ResourceSettingsComponent,
        meta: { page: 'resourceSettings' },
      },
      {
        path: 'clients',
        name: 'settings-clients',
        component: ClientRefreshComponent,
        meta: { page: 'clientRefresh' },
      },
      {
        path: 'prefix',
        name: 'settings-prefix',
        component: PrefixComponent,
        meta: { page: 'prefix' },
      },
      {
        path: 'quoteTemplate',
        name: 'settings-quoteTemplate',
        component: QuoteTemplateComponent,
        meta: { page: 'quoteTemplate' },
      },
      {
        path: 'testExternal',
        name: 'settings-testExternal',
        component: TestExternalComponent,
        meta: { page: 'testExternal' },
      },
    ],
  },
  // Reporting / documents / diagnostics (step 22). Same shape as the settings tabs: the Angular
  // no-search shell renders around them and the content is a <router-view> inside it.
  {
    path: '/reporting',
    name: 'reporting',
    component: ReportingComponent,
    meta: { page: 'reporting' },
  },
  {
    path: '/documents',
    name: 'documents',
    component: DocumentComponent,
    meta: { page: 'document' },
  },
  {
    path: '/diagnostics',
    name: 'diagnostics',
    component: DiagnosticsComponent,
    meta: { page: 'diagnostics' },
  },
  // The quote sheet (step 23a). `from` is a query rather than a ui-router state param, because it
  // decides whether the job window stays open on save and a reload should not lose it.
  {
    path: '/quoting',
    name: 'quoteSheet',
    component: QuoteComponent,
    meta: { page: 'quote' },
  },
  // The resources calendar (step 23b) - the app's home page. The Angular `router-resources` state
  // still owns the shell for `/`; this is the content inside it. `extraPages` credits the views
  // that only ever render here (the job form and its information panel), which the Angular
  // recorder used to do from `VIEW_EXTRA`.
  {
    path: '/',
    name: 'resources',
    component: ResourcesLayoutComponent,
    meta: { page: 'resources', extraPages: ['job', 'jobInformation'] },
  },
]

// The paths vue-router owns. Everything else is Angular's, and a guard on those would be
// pointless at best and an infinite redirect at worst (a signed-in visitor on `/`). The named
// child routes resolve with a trailing slash (`/login/?redirect=…`), so normalise it.
export function isAuthPath(path) {
  const normalised = (path || '').replace(/\/$/, '') || '/'
  return normalised === '/login' || normalised === '/forgotPassword'
}

export const router = createRouter({
  history: createWebHistory(),
  routes: routes,
})

// navigation.js holds a reference so services and components can navigate without importing this
// module (which imports every route component).
setRouter(router)

// The auth guard. There was none to move - auth used to be reactive (the 401 interceptor, and
// LoginCtrl.currentSession() redirecting the other way) - so step 20 writes it.
router.beforeEach(async (to, from, next) => {
  if (!isAuthPath(to.path)) {
    next()
    return
  }

  // currentSession() rather than isLoggedIn(): the latter is getCurrentUser(), which v6 does not
  // answer reliably on a fresh page load - see the note in loginService.js. The old LoginCtrl used
  // currentSession() for exactly this gate, and it is the one that reads the stored tokens.
  let authed = false
  try {
    const session = await loginService.currentSession()
    authed = Boolean(session && session.tokens && session.tokens.idToken)
    if (authed) {
      // The boot session check in main.js can lose a race with Amplify's token store on a fresh
      // load and leave the user unset (the guard's call is the one that wins). Seed the user here
      // too - the profile, menu and roles all read it.
      userStore.setUser(session.tokens.idToken.payload)
    }
  } catch {
    authed = false
  }

  if (authed) {
    if (to.query.redirect) {
      // A saved redirect is a URL, so it gets a full load rather than a router hop.
      goToApp(to.query.redirect)
      next(false)
      return
    }

    // A signed-in visitor has no business on /login. Redirect through the router itself: calling
    // router.push() from inside a guard races the navigation already in flight and the push is
    // dropped, which left the visitor sitting on the auth screen (step 24 - before the shell was
    // Vue, the Angular layout rendered regardless, so the dropped push did not show).
    next('/')
    return
  }

  next()
})

router.afterEach(to => {
  // The directory is recorded here from the route. The old Angular recorder only ever saw
  // ui-router states, and there are none now.
  if (to.meta && to.meta.page) {
    pageView(to.meta.page).catch(() => {})
  }

  // Views that render as part of another page rather than having a route of their own - the job
  // form and its information panel only exist on the resources page.
  if (to.meta && to.meta.extraPages) {
    to.meta.extraPages.forEach(page => pageView(page).catch(() => {}))
  }
})
